/**
 * CPSC FULL Processing Validator
 *
 * Validates FULL (FCP/FGC) processing type submissions for CPSC eFiling.
 */
import type { AppRecordValidateResult, NamePath } from '@xbcb/ui-types';
import { ValidationErrorType } from '@xbcb/ui-types';
import {
  CpscProcessingType,
  EntityRoleCode,
  EntityIdentificationCode,
} from '__generated__/graphql';
import type { PgaValidator } from '../types';
import type { CpscPga, CpscValidationArgs } from './types';
import type {
  CpscItemFormState,
  UsCpscFormState,
} from 'libs/cpsc/transformCpsc';
import type { CpscEntityFormState } from 'libs/cpsc/transformEntities';
import {
  validateItemsCardinality,
  validateItemType,
  validateIntendedUse,
  mergeValidationResults,
  createValidationResult,
  ValidationResult,
} from './cpscCommonValidation';

/** Laboratory role codes (stored in laboratories array) */
const LABORATORY_ROLE_CODES: EntityRoleCode[] = [
  EntityRoleCode.ITL,
  EntityRoleCode.LAB,
  EntityRoleCode.NOL,
];

/**
 * CPSC FULL (FCP/FGC) processing type validator.
 *
 * Validates:
 * - Certificate level: certificateType, certificateDeclaration
 * - Item level: productName, manufactureDate, labTestingInfo.testDate
 * - Cardinality: Beta Pilot constraints (max 1 item, 1 MF, 1 CE, 1 PK)
 * - Entities: required MF, required lab, role-based field validation
 */
export class CpscFullValidator implements PgaValidator<CpscPga> {
  /**
   * Check if this validator applies to the given data.
   * @param {CpscValidationArgs} args - Validation arguments
   * @return {boolean} true if processingType is FULL
   */
  isApplicable({ pga }: CpscValidationArgs): boolean {
    return pga?.processingType === CpscProcessingType.FULL;
  }

  /**
   * Validate CPSC FULL processing data.
   * @param {CpscValidationArgs} args - Validation arguments
   * @return {AppRecordValidateResult} Validation result with validateFields and additionalErrors
   */
  validate({
    pga,
    tariffNamePath,
    pgaFlag,
  }: CpscValidationArgs): AppRecordValidateResult {
    const cpscPath = [...tariffNamePath, 'pga', pgaFlag];
    const items = pga?.items ?? [];

    // Certificate-level validations
    const certificateResult = this.validateCertificate(pga, cpscPath);

    // Common validations using shared helpers
    const itemsCardinalityResult = validateItemsCardinality(items, cpscPath);
    const intendedUseResult = validateIntendedUse(pga, cpscPath);

    // Item-level validations
    const itemResults = items.map((item, index) => {
      const itemPath = [...cpscPath, 'items', index];

      // Common item validations
      const itemTypeResult = validateItemType(item, itemPath);

      // FULL-specific item validations
      const itemFieldsResult = this.validateItemFields(item, itemPath);
      const productIdResult = this.validateProductIdentifiers(
        pga,
        item,
        itemPath,
      );
      const lotResult = this.validateLotInformation(item, itemPath);
      const entitiesResult = this.validateEntities(
        item?.entities ?? [],
        itemPath,
      );

      return mergeValidationResults(
        itemTypeResult,
        itemFieldsResult,
        productIdResult,
        lotResult,
        entitiesResult,
      );
    });

    // Merge all results
    return mergeValidationResults(
      certificateResult,
      itemsCardinalityResult,
      intendedUseResult,
      ...itemResults,
    );
  }

  /**
   * Validate certificate-level fields.
   * @param {UsCpscFormState} pga - CPSC PGA form data
   * @param {NamePath} cpscPath - Path to CPSC fields in form
   * @return {ValidationResult} Validation result
   */
  private validateCertificate(
    pga: UsCpscFormState,
    cpscPath: NamePath,
  ): ValidationResult {
    const result = createValidationResult();

    // certificateType required
    if (!pga?.certificateType) {
      result.validateFields.push([...cpscPath, 'certificateType']);
    }

    // certificateDeclaration.certified must be true
    if (!pga?.certificateDeclaration?.certified) {
      result.additionalErrors.push({
        title: 'Certificate Declaration Required',
        type: ValidationErrorType.GENERAL,
        path: [...cpscPath, 'certificateDeclaration', 'certified'],
        messages: ['You must certify this declaration'],
      });
    }

    return result;
  }

  /**
   * Validate FULL-specific item-level required fields.
   * @param {CpscItemFormState} item - CPSC item form data
   * @param {NamePath} itemPath - Path to item in form
   * @return {ValidationResult} Validation result
   */
  private validateItemFields(
    item: CpscItemFormState,
    itemPath: NamePath,
  ): ValidationResult {
    const result = createValidationResult();

    // productName required
    if (!item.productName) {
      result.validateFields.push([...itemPath, 'productName']);
    }

    // manufactureDate required
    if (!item.manufactureDate) {
      result.validateFields.push([...itemPath, 'manufactureDate']);
    }

    // labTestingInfo.testDate required
    if (!item.labTestingInfo?.testDate) {
      result.validateFields.push([...itemPath, 'labTestingInfo', 'testDate']);
    }

    return result;
  }

  /**
   * Validate product identifier requirement (FULL-specific).
   *
   * Rule: At least ONE of the following must be present:
   * - globalIdentifier.code (at certificate level)
   * - productCodes[].code (at item level)
   * - itemIdentifiers[].number (at item level)
   *
   * @param {UsCpscFormState} pga - CPSC PGA form data
   * @param {CpscItemFormState} item - CPSC item form data
   * @param {NamePath} itemPath - Path to item in form
   * @return {ValidationResult} Validation result
   */
  private validateProductIdentifiers(
    pga: UsCpscFormState,
    item: CpscItemFormState,
    itemPath: NamePath,
  ): ValidationResult {
    const result = createValidationResult();

    // Check globalIdentifier at certificate level
    const hasGlobalIdentifier = Boolean(pga?.globalIdentifier?.code);

    // Check productCodes at item level
    const hasProductCodes = Boolean(
      item.productCodes?.length && item.productCodes.some((pc) => pc?.code),
    );

    // Check itemIdentifiers at item level
    const hasItemIdentifiers = Boolean(
      item.itemIdentifiers?.length &&
        item.itemIdentifiers.some((id) => id?.number),
    );

    // At least one must be present
    if (!hasGlobalIdentifier && !hasProductCodes && !hasItemIdentifiers) {
      result.additionalErrors.push({
        title: 'Product Identifier Required',
        type: ValidationErrorType.GENERAL,
        path: itemPath,
        messages: [
          'At least one product identifier is required: Global Identifier (GTIN/UPC), Product Code (SKU), or Item Identifier (Model/Serial/Registered Number)',
        ],
      });
    }

    return result;
  }

  /**
   * Validate lot information conditional requirement (FULL-specific).
   *
   * Rule: lotNumber required when lotNumberAssigner is provided.
   *
   * @param {CpscItemFormState} item - CPSC item form data
   * @param {NamePath} itemPath - Path to item in form
   * @return {ValidationResult} Validation result
   */
  private validateLotInformation(
    item: CpscItemFormState,
    itemPath: NamePath,
  ): ValidationResult {
    const result = createValidationResult();

    if (!item.lotInformation?.length) return result;

    item.lotInformation.forEach((lot, index) => {
      if (lot?.lotNumberAssigner && !lot?.lotNumber) {
        result.validateFields.push([
          ...itemPath,
          'lotInformation',
          index,
          'lotNumber',
        ]);
        result.additionalErrors.push({
          title: 'Lot Number Required',
          type: ValidationErrorType.GENERAL,
          path: [...itemPath, 'lotInformation', index, 'lotNumber'],
          messages: [
            'Lot number is required when lot number assigner is provided',
          ],
        });
      }
    });

    return result;
  }

  /**
   * Validate entities array - cardinality and role-based fields.
   * @param {CpscEntityFormState[]} entities - Array of entity form data
   * @param {NamePath} itemPath - Path to parent item in form
   * @return {ValidationResult} Validation result
   */
  private validateEntities(
    entities: CpscEntityFormState[],
    itemPath: NamePath,
  ): ValidationResult {
    // Group entities by role
    const manufacturers = entities.filter(
      (e) => e.entityRoleCode === EntityRoleCode.MF,
    );
    const laboratories = entities.filter((e) =>
      LABORATORY_ROLE_CODES.includes(e.entityRoleCode as EntityRoleCode),
    );
    const certifyingEntities = entities.filter(
      (e) => e.entityRoleCode === EntityRoleCode.CE,
    );
    const pointOfContacts = entities.filter(
      (e) => e.entityRoleCode === EntityRoleCode.PK,
    );

    // Cardinality validation
    const cardinalityResult = this.validateEntityCardinality(
      manufacturers,
      laboratories,
      certifyingEntities,
      pointOfContacts,
      itemPath,
    );

    // Role-based field validation for each entity
    const entityResults = entities.map((entity, index) => {
      const entityPath = [...itemPath, 'entities', index];
      return this.validateEntityFields(entity, entityPath);
    });

    return mergeValidationResults(cardinalityResult, ...entityResults);
  }

  /**
   * Validate entity cardinality (CBP Email constraints).
   * @param {CpscEntityFormState[]} manufacturers - Manufacturer entities
   * @param {CpscEntityFormState[]} laboratories - Laboratory entities (ITL/LAB/NOL)
   * @param {CpscEntityFormState[]} certifyingEntities - Certifying entities (CE)
   * @param {CpscEntityFormState[]} pointOfContacts - Point of contact entities (PK)
   * @param {NamePath} itemPath - Path to parent item in form
   * @return {ValidationResult} Validation result
   */
  private validateEntityCardinality(
    manufacturers: CpscEntityFormState[],
    laboratories: CpscEntityFormState[],
    certifyingEntities: CpscEntityFormState[],
    pointOfContacts: CpscEntityFormState[],
    itemPath: NamePath,
  ): ValidationResult {
    const result = createValidationResult();

    // MF: exactly 1 required (Beta Pilot)
    if (manufacturers.length === 0) {
      result.additionalErrors.push({
        title: 'Manufacturer Required',
        type: ValidationErrorType.GENERAL,
        path: [...itemPath, 'entities'],
        messages: ['At least one Manufacturer (MF) is required'],
      });
    }
    if (manufacturers.length > 1) {
      result.additionalErrors.push({
        title: 'CPSC Beta Pilot Constraint',
        type: ValidationErrorType.GENERAL,
        path: [...itemPath, 'entities'],
        messages: ['Beta Pilot: Only one Manufacturer (MF) is allowed'],
      });
    }

    // Laboratory: at least 1 required (ITL/LAB/NOL)
    if (laboratories.length === 0) {
      result.additionalErrors.push({
        title: 'Laboratory Required',
        type: ValidationErrorType.GENERAL,
        path: [...itemPath, 'entities'],
        messages: ['At least one Laboratory (ITL/LAB/NOL) is required'],
      });
    }

    // CE: max 1 (Beta Pilot)
    if (certifyingEntities.length > 1) {
      result.additionalErrors.push({
        title: 'CPSC Beta Pilot Constraint',
        type: ValidationErrorType.GENERAL,
        path: [...itemPath, 'entities'],
        messages: ['Beta Pilot: Only one Certifying Entity (CE) is allowed'],
      });
    }

    // PK: max 1 (Beta Pilot)
    if (pointOfContacts.length > 1) {
      result.additionalErrors.push({
        title: 'CPSC Beta Pilot Constraint',
        type: ValidationErrorType.GENERAL,
        path: [...itemPath, 'entities'],
        messages: ['Beta Pilot: Only one Point of Contact (PK) is allowed'],
      });
    }

    return result;
  }

  /**
   * Validate entity fields based on role code.
   * @param {CpscEntityFormState} entity - Entity form data
   * @param {NamePath} entityPath - Path to entity in form
   * @return {ValidationResult} Validation result
   */
  private validateEntityFields(
    entity: CpscEntityFormState,
    entityPath: NamePath,
  ): ValidationResult {
    const result = createValidationResult();
    const role = entity.entityRoleCode;

    // entityRoleCode is required
    if (!role) {
      result.validateFields.push([...entityPath, 'entityRoleCode']);
      return result;
    }

    switch (role) {
      case EntityRoleCode.MF:
        return mergeValidationResults(
          result,
          this.validateMfEntity(entity, entityPath),
        );
      case EntityRoleCode.ITL:
        return mergeValidationResults(
          result,
          this.validateItlEntity(entity, entityPath),
        );
      case EntityRoleCode.LAB:
        return mergeValidationResults(
          result,
          this.validateLabEntity(entity, entityPath),
        );
      case EntityRoleCode.NOL:
        return mergeValidationResults(
          result,
          this.validateNolEntity(entity, entityPath),
        );
      case EntityRoleCode.CE:
        return mergeValidationResults(
          result,
          this.validateCeEntity(entity, entityPath),
        );
      case EntityRoleCode.PK:
        return mergeValidationResults(
          result,
          this.validatePkEntity(entity, entityPath),
        );
      default:
        return result;
    }
  }

  /**
   * Validate MF (Manufacturer) entity.
   * @param {CpscEntityFormState} entity - Manufacturer entity data
   * @param {NamePath} entityPath - Path to entity in form
   * @return {ValidationResult} Validation result
   */
  private validateMfEntity(
    entity: CpscEntityFormState,
    entityPath: NamePath,
  ): ValidationResult {
    const result = createValidationResult();

    // facility.id required for MF
    if (!entity.facility?.id) {
      result.validateFields.push([...entityPath, 'facility']);
    }

    // If SBM toggle is on, entityNumber required
    if (
      entity.entityIdentificationCode === EntityIdentificationCode.SBM &&
      !entity.entityNumber
    ) {
      result.validateFields.push([...entityPath, 'entityNumber']);
    }

    return result;
  }

  /**
   * Validate ITL (Independent Third Party Laboratory) entity.
   * @param {CpscEntityFormState} entity - ITL entity data
   * @param {NamePath} entityPath - Path to entity in form
   * @return {ValidationResult} Validation result
   */
  private validateItlEntity(
    entity: CpscEntityFormState,
    entityPath: NamePath,
  ): ValidationResult {
    const result = createValidationResult();

    // entityNumber required (4-digit CPSC Lab ID)
    if (!entity.entityNumber) {
      result.validateFields.push([...entityPath, 'entityNumber']);
    }

    // testingInclusions required
    if (!entity.testingInclusions?.length) {
      result.validateFields.push([...entityPath, 'testingInclusions']);
      result.additionalErrors.push({
        title: 'Citation Codes Required',
        type: ValidationErrorType.GENERAL,
        path: [...entityPath, 'testingInclusions'],
        messages: ['At least one citation code is required for ITL'],
      });
    }

    return result;
  }

  /**
   * Validate LAB (Laboratory) entity.
   * @param {CpscEntityFormState} entity - LAB entity data
   * @param {NamePath} entityPath - Path to entity in form
   * @return {ValidationResult} Validation result
   */
  private validateLabEntity(
    entity: CpscEntityFormState,
    entityPath: NamePath,
  ): ValidationResult {
    const result = createValidationResult();

    // facility.id required for LAB
    if (!entity.facility?.id) {
      result.validateFields.push([...entityPath, 'facility']);
    }

    // testingInclusions required
    if (!entity.testingInclusions?.length) {
      result.validateFields.push([...entityPath, 'testingInclusions']);
      result.additionalErrors.push({
        title: 'Citation Codes Required',
        type: ValidationErrorType.GENERAL,
        path: [...entityPath, 'testingInclusions'],
        messages: ['At least one citation code is required for LAB'],
      });
    }

    return result;
  }

  /**
   * Validate NOL (No Lab Testing Required) entity.
   * @param {CpscEntityFormState} entity - NOL entity data
   * @param {NamePath} entityPath - Path to entity in form
   * @return {ValidationResult} Validation result
   */
  private validateNolEntity(
    entity: CpscEntityFormState,
    entityPath: NamePath,
  ): ValidationResult {
    const result = createValidationResult();

    // testingExclusions required
    if (!entity.testingExclusions?.length) {
      result.validateFields.push([...entityPath, 'testingExclusions']);
      result.additionalErrors.push({
        title: 'Testing Exclusion Required',
        type: ValidationErrorType.GENERAL,
        path: [...entityPath, 'testingExclusions'],
        messages: [
          'At least one testing exclusion citation code is required for NOL',
        ],
      });
    }

    return result;
  }

  /**
   * Validate CE (Certifying Entity) entity.
   * @param {CpscEntityFormState} entity - CE entity data
   * @param {NamePath} entityPath - Path to entity in form
   * @return {ValidationResult} Validation result
   */
  private validateCeEntity(
    entity: CpscEntityFormState,
    entityPath: NamePath,
  ): ValidationResult {
    const result = createValidationResult();

    // facility.id required for CE
    if (!entity.facility?.id) {
      result.validateFields.push([...entityPath, 'facility']);
    }

    return result;
  }

  /**
   * Validate PK (Point of Contact) entity.
   * @param {CpscEntityFormState} entity - PK entity data
   * @param {NamePath} entityPath - Path to entity in form
   * @return {ValidationResult} Validation result
   */
  private validatePkEntity(
    entity: CpscEntityFormState,
    entityPath: NamePath,
  ): ValidationResult {
    const result = createValidationResult();

    // facility.id required for PK
    if (!entity.facility?.id) {
      result.validateFields.push([...entityPath, 'facility']);
    }

    return result;
  }
}
